import React, { useState, useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import { ArrowUpDown, Download, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { db } from '../db';
import { supabase } from '../lib/supabase';
import type { SKU } from '../types';
import * as XLSX from 'xlsx';
import ImportModal from './ImportModal';
import useSound from 'use-sound';

const completeSound = 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3';

const columnHelper = createColumnHelper<SKU>();

const columns = [
  columnHelper.accessor('id', {
    header: 'SKU',
    cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
  }),
  columnHelper.accessor('expectedQuantity', {
    header: '예상 수량',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('actualQuantity', {
    header: '실제 수량',
    cell: (info) => {
      const actual = info.getValue() || 0;
      const expected = info.row.original.expectedQuantity;
      const diff = actual - expected;

      let textColorClass = 'text-gray-900';
      let diffText = '';

      if (diff < 0) {
        textColorClass = 'text-red-600';
        diffText = ` (${diff})`;
      } else if (diff > 0) {
        textColorClass = 'text-blue-600';
        diffText = ` (+${diff})`;
      }

      return <span className={textColorClass}>{actual}{diffText}</span>;
    },
  }),
  columnHelper.accessor('progress', {
    header: '진행률',
    cell: (info) => {
      const progress = info.getValue() || 0;
      const actual = info.row.original.actualQuantity || 0;
      const expected = info.row.original.expectedQuantity;

      let bgColorClass = 'bg-blue-500';
      if (actual > expected) bgColorClass = 'bg-blue-600';
      else if (actual < expected && actual > 0) bgColorClass = 'bg-red-500';
      else if (actual === expected) bgColorClass = 'bg-green-500';

      return (
        <div className="flex items-center gap-2">
          <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${bgColorClass}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <span className="text-sm font-medium w-12 text-right">{progress.toFixed(0)}%</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: '상태',
    cell: (info) => {
      const row = info.row.original;
      const actual = row.actualQuantity || 0;
      const expected = row.expectedQuantity;
      const [playComplete] = useSound(completeSound, { volume: 0.5 });

      const handleComplete = async () => {
        if (actual < expected) {
          const confirm = window.confirm(
            `예정 수량(${expected})에 미달된 상태(${actual})입니다. 입고를 완료하시겠습니까?`
          );
          if (!confirm) return;
        }

        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('로그인 세션이 없습니다.');

          const now = new Date().toISOString();
          const updates = { status: 'completed', progress: 100, last_modified: now };

          const { error: updateError } = await supabase
            .from('skus')
            .update(updates)
            .eq('id', row.id);

          if (updateError) throw new Error(`Supabase 업데이트 실패: ${updateError.message}`);

          await db.skus.update(row.id, {
            status: 'completed',
            progress: 100,
            lastModified: new Date(now),
          });

          playComplete();
          toast.success(`${row.id} 입고가 완료되었습니다!`, { icon: '🎉' });
        } catch (error: any) {
          console.error('상태 업데이트 실패:', error.message);
          toast.error(`상태 업데이트 실패: ${error.message}`);
        }
      };

      const getStatusDisplay = () => {
        if (info.getValue() === 'completed') {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs md:text-sm font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-4 h-4 mr-1" />
              완료
            </span>
          );
        }

        const buttonClass = actual === 0
          ? 'bg-gray-100 text-gray-800 cursor-not-allowed'
          : actual < expected
          ? 'bg-red-100 text-red-800 hover:bg-red-200'
          : actual > expected
          ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
          : 'bg-green-100 text-green-800 hover:bg-green-200';

        return (
          <button
            onClick={handleComplete}
            disabled={actual === 0}
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs md:text-sm font-medium transition-colors ${buttonClass}`}
          >
            {actual === 0 ? '미처리' : '완료하기'}
          </button>
        );
      };

      return getStatusDisplay();
    },
  }),
  columnHelper.accessor('lastScanned', {
    header: '마지막 스캔',
    cell: (info) => info.getValue()?.toLocaleString() || '-',
  }),
];

export default function InventoryTable() {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'lastScanned', desc: true }]);
  const [showImport, setShowImport] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);

  const fetchSkus = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: skus, error } = await supabase
        .from('skus')
        .select('*')
        .or(`user_id.eq.${session.user.id},user_id.is.null`)
        .order('last_scanned', { ascending: false });

      if (error) throw new Error(`SKU 조회 실패: ${error.message}`);

      await db.transaction('rw', db.skus, async () => {
        if (skus && skus.length > 0) {
          await Promise.all(
            skus.map(async (sku) => {
              const localSku = await db.skus.get(sku.id);
              if (!localSku || localSku.lastModified < new Date(sku.last_modified)) {
                await db.skus.put({
                  id: sku.id,
                  styleNo: sku.style_no,
                  color: sku.color,
                  size: sku.size,
                  expectedQuantity: sku.expected_quantity,
                  actualQuantity: sku.actual_quantity,
                  status: sku.status,
                  progress: sku.progress,
                  lastScanned: new Date(sku.last_scanned),
                  lastModified: new Date(sku.last_modified),
                  userId: sku.user_id,
                });
              }
            })
          );
        }
      });
    } catch (error: any) {
      console.error('SKU 데이터 동기화 실패:', error.message);
      toast.error(`데이터 동기화 실패: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkus();
    const interval = setInterval(fetchSkus, 30000);
    return () => clearInterval(interval);
  }, [fetchSkus]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const skus = useLiveQuery(() => db.skus.orderBy('lastScanned').reverse().toArray(), []);

  const table = useReactTable({
    data: skus || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const exportToExcel = () => {
    try {
      if (!skus?.length) {
        toast.error('내보낼 데이터가 없습니다');
        return;
      }

      const excelData = skus.map((sku) => {
        const actual = sku.actualQuantity || 0;
        const expected = sku.expectedQuantity;
        let result = '정상';
        if (actual < expected) result = '부족';
        else if (actual > expected) result = '과입';

        return {
          '스타일NO.': sku.styleNo,
          '색상': sku.color,
          '사이즈': sku.size,
          '예정수량': sku.expectedQuantity,
          '스캔수량': actual,
          '진행률': `${Math.min((actual / expected) * 100, 100).toFixed(1)}%`,
          '결과': result,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 15 },
        { wch: 10 },
        { wch: 8 },
        { wch: 10 },
        { wch: 10 },
        { wch: 8 },
        { wch: 8 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '입고현황');

      const now = new Date();
      const fileName = `입고현황_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      toast.success('엑셀 파일이 다운로드되었습니다');
    } catch (error: any) {
      console.error('엑셀 내보내기 실패:', error.message);
      toast.error('엑셀 파일 생성에 실패했습니다');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">입고 처리 현황</h2>
            <p className="text-sm text-gray-500">
              {currentTime.toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <button
              onClick={() => setShowImport(true)}
              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors text-sm md:text-base"
            >
              <Upload className="w-4 h-4" />
              <span className="whitespace-nowrap">엑셀 업로드</span>
            </button>
            <button
              onClick={exportToExcel}
              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors text-sm md:text-base"
            >
              <Download className="w-4 h-4" />
              <span className="whitespace-nowrap">엑셀 다운로드</span>
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-2 text-gray-500">데이터를 불러오는 중...</p>
          </div>
        ) : (
          <>
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center gap-2 ${
                              header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                            }`}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                            {header.column.getCanSort() && <ArrowUpDown className="w-4 h-4" />}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-500"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {(!skus || skus.length === 0) && (
              <div className="text-center py-12 text-gray-500">데이터가 없습니다</div>
            )}
          </>
        )}
      </div>

      <ImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImportSuccess={() => fetchSkus()}
      />
    </div>
  );
}